import { useState, useEffect, useMemo } from "react";
import Modal from "../ui/Modal";
import Input from "../ui/Input";
import Select from "../ui/Select";
import Button from "../ui/Button";
import { toISO, today, monthKeyFromDate, fromISO } from "../../lib/dateUtils";
import { formatNumber } from "../../lib/formatters";

// Find the most recent FX rate at or before `monthKey`. If none, fall
// back to the latest rate available. Returns null if no rates exist.
function pickRate(fxRates, monthKey) {
  if (!fxRates || fxRates.length === 0) return null;
  const sorted = [...fxRates].sort((a, b) => a.month.localeCompare(b.month));
  const exact = sorted.find((r) => r.month === monthKey);
  if (exact) return exact;
  const prior = [...sorted].reverse().find((r) => r.month <= monthKey);
  if (prior) return prior;
  return sorted[0];
}

const blank = () => ({
  contractorId: "",
  date: toISO(today()),
  usdAmount: 0,
  nativeAmount: null,
  nativeCurrency: "USD",
  fxRate: null,
  notes: "",
});

export default function ContractorPaymentForm({
  open, onClose, onSubmit, initial, contractors = [], fxRates = [], lockedContractorId = null,
}) {
  const [form, setForm] = useState(() => initial || blank());
  const [errors, setErrors] = useState({});
  const [inputCurrency, setInputCurrency] = useState("USD");
  const [amountInput, setAmountInput] = useState("");

  useEffect(() => {
    if (!open) return;
    const initForm = initial || blank();
    if (lockedContractorId) initForm.contractorId = lockedContractorId;
    setForm(initForm);
    setErrors({});
    if (initial?.nativeCurrency && initial?.nativeAmount != null) {
      setInputCurrency(initial.nativeCurrency);
      setAmountInput(String(initial.nativeAmount));
    } else {
      setInputCurrency("USD");
      setAmountInput(initial?.usdAmount ? String(initial.usdAmount) : "");
    }
  }, [open, initial, lockedContractorId]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // FX rate for the date entered — mirrors employee salary logic.
  const monthKey = useMemo(() => {
    if (!form.date) return null;
    try { return monthKeyFromDate(fromISO(form.date)); } catch { return null; }
  }, [form.date]);
  const rate = useMemo(() => {
    if (!monthKey) return null;
    return pickRate(fxRates, monthKey)?.usdToEgp || null;
  }, [fxRates, monthKey]);

  const usdFromInput = useMemo(() => {
    const v = Number(amountInput);
    if (!Number.isFinite(v) || v <= 0) return 0;
    if (inputCurrency === "EGP" && rate && rate > 0) return v / rate;
    return v;
  }, [amountInput, inputCurrency, rate]);

  const handleAmountChange = (e) => {
    setAmountInput(e.target.value);
  };

  const toggleCurrency = (next) => {
    if (next === inputCurrency) return;
    if (rate && rate > 0) {
      const v = Number(amountInput);
      if (Number.isFinite(v) && v > 0) {
        const converted = next === "EGP" ? v * rate : v / rate;
        setAmountInput(String(Math.round(converted * 100) / 100));
      }
    }
    setInputCurrency(next);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.contractorId) errs.contractorId = "Pick a contractor";
    if (!form.date) errs.date = "Required";
    const amt = Number(amountInput);
    if (!Number.isFinite(amt) || amt <= 0) errs.amount = "Enter an amount > 0";
    if (inputCurrency === "EGP" && (!rate || rate <= 0)) {
      errs.amount = "No FX rate set for this month — go to Settings → Currency exchange.";
    }
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSubmit({
      ...form,
      usdAmount: Math.round(usdFromInput * 100) / 100,
      nativeAmount: amt,
      nativeCurrency: inputCurrency,
      fxRate: inputCurrency === "EGP" ? rate : null,
    });
    onClose();
  };

  let conversionHint = null;
  if (amountInput && Number(amountInput) > 0) {
    if (inputCurrency === "EGP") {
      if (rate && rate > 0) {
        conversionHint = (
          <span className="text-emerald-700 dark:text-emerald-300">
            ≈ ${formatNumber(usdFromInput, 2)} USD at {formatNumber(rate, 2)} EGP/USD
          </span>
        );
      } else {
        conversionHint = (
          <span className="text-red-600">No FX rate for this month — go to Settings → Currency exchange.</span>
        );
      }
    } else if (rate && rate > 0) {
      conversionHint = (
        <span className="text-brand-slate">
          ≈ {formatNumber(Number(amountInput) * rate, 0)} EGP at {formatNumber(rate, 2)} EGP/USD
        </span>
      );
    }
  }

  const activeContractors = contractors.filter((c) => c.status === "active");

  return (
    <Modal open={open} onClose={onClose} title={initial ? "Edit payment" : "Add contractor payment"}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <Select
            label="Contractor *"
            value={form.contractorId}
            onChange={(e) => update("contractorId", e.target.value)}
            disabled={!!lockedContractorId}
          >
            <option value="">— Select —</option>
            {activeContractors.map((c) => (
              <option key={c.id} value={c.id}>{c.name} {c.role ? `· ${c.role}` : ""}</option>
            ))}
          </Select>
          {errors.contractorId && <p className="text-[11px] text-red-600 mt-1">{errors.contractorId}</p>}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Payment date *"
            type="date"
            value={form.date}
            onChange={(e) => update("date", e.target.value)}
            error={errors.date}
          />
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-brand-slate">Amount *</span>
              <div className="inline-flex items-center bg-gray-100 dark:bg-[var(--bg-soft)] rounded-md p-0.5 text-[10px] font-semibold">
                <button
                  type="button"
                  onClick={() => toggleCurrency("USD")}
                  className={`px-2 py-0.5 rounded ${inputCurrency === "USD" ? "bg-white dark:bg-[var(--bg-surface)] text-brand-ink dark:text-[var(--text-primary)] shadow-sm" : "text-brand-slate"}`}
                >USD</button>
                <button
                  type="button"
                  onClick={() => toggleCurrency("EGP")}
                  className={`px-2 py-0.5 rounded ${inputCurrency === "EGP" ? "bg-white dark:bg-[var(--bg-surface)] text-brand-ink dark:text-[var(--text-primary)] shadow-sm" : "text-brand-slate"}`}
                >EGP</button>
              </div>
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-slate text-sm pointer-events-none">
                {inputCurrency === "USD" ? "$" : "E£"}
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amountInput}
                onChange={handleAmountChange}
                placeholder="0"
                className={`w-full pl-8 pr-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30 dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)] ${errors.amount ? "border-red-400" : "border-gray-200"}`}
              />
            </div>
            <div className="mt-1 min-h-[1rem] text-[11px]">
              {errors.amount ? <span className="text-red-600">{errors.amount}</span> : conversionHint}
            </div>
          </div>
        </div>

        <label className="block">
          <span className="block text-xs font-medium text-brand-slate mb-1">Notes</span>
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            placeholder="e.g. May video edit · 3 reels"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30 dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)]"
          />
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit">{initial ? "Save" : "Add payment"}</Button>
        </div>
      </form>
    </Modal>
  );
}